import React from "react";
import Button from "../common/Button";

const formatDateInputValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseStartOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const parseEndOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

/**
 * FilterBar - Shared filter component for all reporting tabs
 * Manages date range, channel, and limit filters
 */
const FilterBar = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  channel,
  onChannelChange,
  showChannel = true,
  channelOptions,
  showSearch = false,
  search = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  presets = [],
  activePreset = "",
  onPresetChange,
  showStatus = false,
  status = "",
  onStatusChange,
  statusOptions = [],
  limit,
  onLimitChange,
  showLimit = false,
  onApply,
  onRefresh,
  onReset,
  isLoading = false,
  validationMessage = "",
  fieldOrder = ["from", "to", "channel", "search", "status", "limit"],
  customFields = [],
  searchFieldClassName = "",
  className = "",
}) => {
  const resolvedChannelOptions =
    Array.isArray(channelOptions) && channelOptions.length > 0
      ? channelOptions
      : [
          { value: "KIOSK", label: "Kiosk" },
          { value: "POS", label: "POS" },
          { value: "ONLINE", label: "Online" },
          // { value: "ALL", label: "All Channels" },
        ];

  const fieldInputClassName =
    "h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100";

  const customFieldMap = Object.fromEntries(
    customFields
      .filter((field) => field && field.key && field.node)
      .map((field) => [field.key, field.node]),
  );

  const fieldMap = {
    from: (
      <div key="from" className="min-w-[150px]">
        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
          From
        </label>
        <input
          type="date"
          value={formatDateInputValue(startDate)}
          max={formatDateInputValue(endDate)}
          onChange={(e) => onStartDateChange(parseStartOfDay(e.target.value))}
          className={fieldInputClassName}
        />
      </div>
    ),
    to: (
      <div key="to" className="min-w-[150px]">
        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
          To
        </label>
        <input
          type="date"
          value={formatDateInputValue(endDate)}
          min={formatDateInputValue(startDate)}
          onChange={(e) => onEndDateChange(parseEndOfDay(e.target.value))}
          className={fieldInputClassName}
        />
      </div>
    ),
    channel: showChannel ? (
      <div key="channel" className="min-w-[140px]">
        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
          Channel
        </label>
        <select
          value={channel}
          onChange={(e) => onChannelChange(e.target.value)}
          className={fieldInputClassName}
        >
          {resolvedChannelOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    ) : null,
    search: showSearch ? (
      <div
        key="search"
        className={`min-w-[220px] flex-1 ${searchFieldClassName}`}
      >
        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
          Search
        </label>
        <input
          type="search"
          inputMode="search"
          enterKeyHint="search"
          value={search}
          onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className={fieldInputClassName}
        />
      </div>
    ) : null,
    status: showStatus ? (
      <div key="status" className="min-w-[170px]">
        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
          Status
        </label>
        <select
          value={status}
          onChange={(e) => onStatusChange && onStatusChange(e.target.value)}
          className={fieldInputClassName}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    ) : null,
    limit: showLimit ? (
      <div key="limit" className="w-[120px]">
        <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
          Limit
        </label>
        <input
          type="text"
          inputMode="numeric"
          enterKeyHint="done"
          pattern="[0-9]*"
          value={limit}
          onChange={(e) => onLimitChange(e.target.value)}
          className={fieldInputClassName}
        />
      </div>
    ) : null,
    ...customFieldMap,
  };

  const orderedFields = fieldOrder
    .map((fieldKey) => fieldMap[fieldKey])
    .filter(Boolean);

  return (
    <div
      className={`rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 shadow-sm ${className}`}
    >
      {presets.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {presets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => onPresetChange && onPresetChange(preset.value)}
              className={[
                "h-8 rounded-full border px-3 text-xs font-semibold transition",
                activePreset === preset.value
                  ? "border-indigo-400 bg-indigo-100 text-indigo-700 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300"
                  : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        {orderedFields}

        <div className="ml-auto flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="h-9 rounded border border-slate-200 dark:border-slate-700 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Refresh
            </button>
          )}
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="h-9 rounded border border-slate-200 dark:border-slate-700 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Reset
            </button>
          )}
          {onApply && (
            <Button size="sm" onClick={onApply} disabled={isLoading}>
              {isLoading ? "Loading..." : "Apply"}
            </Button>
          )}
        </div>
      </div>
      {validationMessage && (
        <p className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400">
          {validationMessage}
        </p>
      )}
    </div>
  );
};

export default FilterBar;
